"use client";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLocale } from "next-intl";
import { useGetAbout } from "@/hooks/main-hooks";

const CEOMessage = () => {
  const locale = useLocale();
  const isAr = locale === "ar";

  const { data: about } = useGetAbout();
  const ceo = about?.ceoMessage;

  const content = {
    en: {
      title: "A Word from the CEO",
      name: "Haitham El Maghrabi",
      role: "Chief Executive Officer, Al Dawlia",
      heritage: "Heritage of 32 Years",
      body: [
        "For over 32 years, we have built the name of Al Dawlia 21 on a solid foundation: we are partners in quality with our clients—and they are partners in our success. From this shared commitment, lasting trust has been established and continuously strengthened.",
        "In every project we support—from residential developments to complex architectural applications—our commitment remains unwavering: to deliver reliable aluminum solutions that meet the highest standards of quality, performance, and durability.",
        "We firmly believe that trust is not built through words, but through the ability to deliver. It is earned through engineering discipline, strong manufacturing capabilities, and the dedication of teams who fully understand the responsibility behind every project—regardless of its size.",
      ],
      footerText:
        "Engineering Discipline • Manufacturing Power • Global Standards",
    },
    ar: {
      title: "كلمة الرئيس التنفيذي",
      name: "هيثم المغربي",
      role: "الرئيس التنفيذي، الدولية",
      heritage: "أكثر من 32 عاماً من الخبرة",
      body: [
        "على مدار أكثر من 32 عاماً، بنينا اسم 'الدولية 21' على أساس راسخ: نحن شركاء في الجودة مع عملائنا، وهم شركاء في نجاحنا. ومن هذا الالتزام المشترك، تأسست ثقة دائمة وتزداد قوة يوماً بعد يوم.",
        "في كل مشروع ندعمه - من المجمعات السكنية إلى التطبيقات المعمارية المعقدة - يظل التزامنا ثابتاً: تقديم حلول ألومنيوم موثوقة تلبي أعلى معايير الجودة والأداء والمتانة.",
        "نحن نؤمن إيماناً راسخاً بأن الثقة لا تُبنى بالكلمات، بل بالقدرة على الإنجاز. إنها تُكتسب من خلال الانضباط الهندسي، والقدرات التصنيعية القوية، وتفاني الفرق التي تدرك تماماً المسؤولية الكامنة وراء كل مشروع.",
      ],
      footerText: "انضباط هندسي • قوة تصنيعية • معايير عالمية",
    },
  };

  const fallback = isAr ? content.ar : content.en;

  // Merge dashboard content over the static fallback, field by field.
  const ceoBody = isAr ? ceo?.bodyAr : ceo?.bodyEn;
  const bodyParagraphs = ceoBody
    ? ceoBody.split("\n").filter((p: string) => p.trim())
    : fallback.body;

  const active = {
    title: (isAr ? ceo?.titleAr : ceo?.titleEn) || fallback.title,
    name: (isAr ? ceo?.nameAr : ceo?.nameEn) || fallback.name,
    role: (isAr ? ceo?.roleAr : ceo?.roleEn) || fallback.role,
    heritage: fallback.heritage,
    body: bodyParagraphs,
    footerText: fallback.footerText,
  };

  return (
    <section
      dir={isAr ? "rtl" : "ltr"}
      className="bg-brand-main min-h-screen py-20 px-6 lg:px-20 overflow-hidden font-sans"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
          // التنسيق بيتغير ديناميكياً حسب اللغة
          className={`relative p-8 lg:p-16 shadow-2xl transition-all duration-500 ${
            isAr
              ? "bg-main border-r-8 border-accent rounded-tl-[100px] text-main-white"
              : "bg-main-white border-l-8 border-accent rounded-tr-[100px] text-text-body"
          }`}
        >
          {/* Quote Icon خلفية شفافة */}
          <Quote
            className={`absolute top-6 w-32 h-32 opacity-10 z-0 ${
              isAr ? "left-8 rotate-180 text-accent" : "right-8 text-accent"
            }`}
          />

          <div className="relative z-10">
            {/* Heritage Label */}
            <span className="text-accent font-bold tracking-[0.2em] text-sm mb-4 block uppercase">
              {active.heritage}
            </span>

            {/* Title */}
            <h2
              className={`text-4xl lg:text-6xl font-black mb-10 leading-tight tracking-tight ${
                isAr ? "text-main-white" : "text-main"
              }`}
            >
              {active.title}
            </h2>

            {/* Body Text */}
            <div
              className={`space-y-8 text-lg lg:text-xl leading-[1.8] ${
                isAr
                  ? "text-brand-main/90 font-light"
                  : "text-text-body font-normal"
              }`}
            >
              {active.body.map((p: string, i: number) => (
                <p key={i}>{p}</p>
              ))}
            </div>

            {/* CEO Info Area */}
            <div
              className={`mt-14 pt-10 border-t ${
                isAr ? "border-accent/30" : "border-secondary"
              }`}
            >
              <h4
                className={`text-2xl lg:text-3xl font-bold tracking-tight ${
                  isAr ? "text-main-white" : "text-primary"
                }`}
              >
                {active.name}
              </h4>
              <p className="text-accent font-medium text-lg mt-1">
                {active.role}
              </p>

              {/* Signature Stamp - لمسة بريميوم */}
              <div className="mt-10 opacity-30 grayscale hover:grayscale-0 transition-all duration-500">
                <div className="border-2 border-accent text-accent inline-block px-6 py-2 rotate-[-8deg] font-serif text-xs uppercase tracking-[0.3em]">
                  Al Dawlia Certified Quality
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer Line */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-16 flex flex-col items-center text-center"
        >
          <div className="w-px h-16 bg-linear-to-b from-accent to-transparent mb-6"></div>
          <p className="text-dark/40 uppercase tracking-[0.4em] text-[10px] lg:text-xs font-bold px-4">
            {active.footerText}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default CEOMessage;
